import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Target, Repeat, TrendingUp, Trophy, ArrowUp, ArrowDown, Minus, ChevronDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts';

const SKILL_NAMES = {
  risk_management: { de: 'Risiko-Management', en: 'Risk Management', icon: Shield, color: '#06b6d4' },
  consistency: { de: 'Konsistenz', en: 'Consistency', icon: Repeat, color: '#8b5cf6' },
  process_discipline: { de: 'Prozess-Disziplin', en: 'Process Discipline', icon: Target, color: '#f59e0b' },
  profitability: { de: 'Profitabilität', en: 'Profitability', icon: TrendingUp, color: '#10b981' },
};

const RANK_COLORS = {
  'Einsteiger': '#ef4444',
  'Stabil': '#f59e0b',
  'Fortgeschritten': '#eab308',
  'Professionell': '#22c55e',
  'Experte': '#06b6d4',
  'Elite': '#a855f7',
};

const getRankName = (score, language = 'de') => {
  const ranks_de = {
    0: 'Einsteiger',
    1000: 'Stabil',
    1200: 'Fortgeschritten',
    1400: 'Professionell',
    1600: 'Experte',
    1800: 'Elite'
  };
  const ranks_en = {
    0: 'Beginner',
    1000: 'Stable',
    1200: 'Advanced',
    1400: 'Professional',
    1600: 'Expert',
    1800: 'Elite'
  };
  
  const ranks = language === 'de' ? ranks_de : ranks_en;
  
  for (const threshold of Object.keys(ranks).sort((a, b) => b - a)) {
    if (score >= threshold) {
      return ranks[threshold];
    }
  }
  return ranks[0];
};

export const SkillScoreCard = ({ skillKey, score, delta, reasoning }) => {
  const { language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const skill = SKILL_NAMES[skillKey];
  const Icon = skill?.icon || Shield;
  
  const getDeltaDisplay = () => {
    if (!delta || delta === 0) return <Minus className="h-4 w-4 text-muted-foreground" />;
    if (delta > 0) return <ArrowUp className="h-4 w-4 text-emerald-400" />;
    return <ArrowDown className="h-4 w-4 text-rose-400" />;
  };

  // Show expand option if reasoning text exists and is longer than ~2 lines (approx 60 chars)
  const hasReasoning = reasoning && reasoning.length > 0;

  return (
    <div 
      className={`p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 transition-all duration-300 ${
        hasReasoning ? 'cursor-pointer hover:border-zinc-600 hover:bg-zinc-800/50' : ''
      } ${isExpanded ? 'border-zinc-600 bg-zinc-800/30' : ''}`}
      onClick={() => hasReasoning && setIsExpanded(!isExpanded)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${skill?.color}20` }}>
            <Icon className="h-4 w-4" style={{ color: skill?.color }} />
          </div>
          <span className="font-medium text-sm">{skill?.[language] || skillKey}</span>
        </div>
        <div className="flex items-center gap-1">
          {getDeltaDisplay()}
          {delta !== 0 && delta && (
            <span className={`text-sm font-mono ${delta > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {delta > 0 ? '+' : ''}{delta}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-end gap-2">
        <span className="text-3xl font-mono font-bold" style={{ color: skill?.color }}>{score}</span>
        <span className="text-sm text-muted-foreground mb-1">Punkte</span>
      </div>
      
      {/* Progress bar to next level */}
      <div className="mt-3">
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${Math.min(100, (score % 200) / 2)}%`,
              backgroundColor: skill?.color 
            }}
          />
        </div>
      </div>
      
      {hasReasoning && (
        <div className="mt-3">
          <p className={`text-xs text-muted-foreground leading-relaxed transition-all duration-300 ${
            isExpanded ? '' : 'line-clamp-2'
          }`}>
            {reasoning}
          </p>
          <div className="flex items-center justify-center mt-2">
            <span className="text-[10px] text-zinc-500 flex items-center gap-1 hover:text-zinc-400 transition-colors">
              <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
              {isExpanded 
                ? (language === 'de' ? 'Weniger anzeigen' : 'Show less')
                : (language === 'de' ? 'Mehr lesen' : 'Read more')
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const OverallScoreDisplay = ({ score, rank, confidence, isDefault, scores }) => {
  const { language } = useLanguage();
  const rankColor = RANK_COLORS[rank] || '#06b6d4';
  
  // Convert scores to percentage for radar chart (assume 2000 is max)
  const normalizeScore = (s) => Math.min(100, Math.max(0, (s / 2000) * 100));
  
  const radarData = [
    { skill: 'RM', value: normalizeScore(scores?.risk_management || 1000), fullMark: 100 },
    { skill: 'KC', value: normalizeScore(scores?.consistency || 1000), fullMark: 100 },
    { skill: 'PD', value: normalizeScore(scores?.process_discipline || 1000), fullMark: 100 },
    { skill: 'PR', value: normalizeScore(scores?.profitability || 1000), fullMark: 100 },
  ];
  
  return (
    <Card className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border-zinc-800">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'de' ? 'Gesamtscore' : 'Overall Score'}
            </p>
            <div className="flex items-end gap-3">
              <span className="text-5xl font-mono font-bold text-white">{score}</span>
              <div className="mb-2">
                <span 
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
                  style={{ backgroundColor: `${rankColor}20`, color: rankColor }}
                >
                  <Trophy className="h-4 w-4" />
                  {rank}
                </span>
              </div>
            </div>
            {isDefault && (
              <p className="mt-2 text-xs text-amber-400">
                {language === 'de' 
                  ? '⚡ Starte eine Monats-Analyse um deinen Score zu berechnen'
                  : '⚡ Start a month analysis to calculate your score'}
              </p>
            )}
            {!isDefault && confidence && confidence !== 'normal' && (
              <p className="mt-2 text-xs text-muted-foreground">
                {confidence === 'low' 
                  ? (language === 'de' ? '⚠️ Niedrige Konfidenz (wenige Trades)' : '⚠️ Low confidence (few trades)')
                  : (language === 'de' ? '✓ Hohe Konfidenz' : '✓ High confidence')
                }
              </p>
            )}
          </div>
          
          {/* Mini radar chart */}
          <div className="w-32 h-32">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#27272a" />
                <PolarAngleAxis dataKey="skill" tick={{ fill: '#71717a', fontSize: 10 }} />
                <Radar dataKey="value" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const SkillHistoryChart = ({ history }) => {
  const { language } = useLanguage();
  
  const chartData = useMemo(() => {
    if (!history || history.length === 0) return [];
    
    return history.slice().reverse().map((h) => ({
      month: `${h.month}/${h.year}`,
      risk: h.risk_management,
      consistency: h.consistency,
      process: h.process_discipline,
      profit: h.profitability,
      overall: h.overall_score
    }));
  }, [history]);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {language === 'de' ? 'Entwicklung über Zeit' : 'Progress Over Time'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis dataKey="month" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} domain={['dataMin - 50', 'dataMax + 50']} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Line type="monotone" dataKey="overall" stroke="#fff" strokeWidth={2} dot={false} name="Gesamt" />
              <Line type="monotone" dataKey="risk" stroke="#06b6d4" strokeWidth={1} dot={false} name="Risiko" />
              <Line type="monotone" dataKey="consistency" stroke="#8b5cf6" strokeWidth={1} dot={false} name="Konsistenz" />
              <Line type="monotone" dataKey="process" stroke="#f59e0b" strokeWidth={1} dot={false} name="Prozess" />
              <Line type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={1} dot={false} name="Profit" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 justify-center">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-white" />
            <span className="text-xs text-muted-foreground">{language === 'de' ? 'Gesamt' : 'Overall'}</span>
          </div>
          {Object.entries(SKILL_NAMES).map(([key, skill]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: skill.color }} />
              <span className="text-xs text-muted-foreground">{skill[language]}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const SkillScoreGrid = ({ scores, deltas, reasoning }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <SkillScoreCard 
        skillKey="risk_management" 
        score={scores?.risk_management || 1000} 
        delta={deltas?.risk_management}
        reasoning={reasoning?.risk_management}
      />
      <SkillScoreCard 
        skillKey="consistency" 
        score={scores?.consistency || 1000} 
        delta={deltas?.consistency}
        reasoning={reasoning?.consistency}
      />
      <SkillScoreCard 
        skillKey="process_discipline" 
        score={scores?.process_discipline || 1000} 
        delta={deltas?.process_discipline}
        reasoning={reasoning?.process_discipline}
      />
      <SkillScoreCard 
        skillKey="profitability" 
        score={scores?.profitability || 1000} 
        delta={deltas?.profitability}
        reasoning={reasoning?.profitability}
      />
    </div>
  );
};

export default { SkillScoreCard, OverallScoreDisplay, SkillHistoryChart, SkillScoreGrid };
